/**
 * The ledger step's target table model (2.J deliverable 15), pure. A target is a secret or a page,
 * keyed `secret:<id>` or `document:<id>`. The selection is a set of keys that no filter touches:
 * "Select all shown" adds the rows the filters show, "Clear selection" empties it, and the
 * request's targets are exactly the selection (each once, in the table's order, then the rest).
 */
import {
  BOOTSTRAP_LEDGER_MAX,
  type BootstrapLedgerTargetV1,
  type BootstrapLedgerV1,
} from '@codex/contracts';

export interface TargetRow {
  readonly key: string;
  readonly kind: 'secret' | 'document';
  readonly id: string;
  /** A secret's label (its text's start when it has none), or the page's title. */
  readonly title: string;
  /** A secret's holder page, or the page's type. */
  readonly detail: string;
  /** Secret: unknown, partial, known. Page: its discovery or play state, `none` when unset. */
  readonly state: string;
}

export const targetKey = (kind: TargetRow['kind'], id: string): string => `${kind}:${id}`;

export interface TargetFilter {
  readonly text: string;
  /** `all`, or one state. */
  readonly state: string;
}

export const NO_FILTER: TargetFilter = { text: '', state: 'all' };

/** The rows the filters show: title or holder contains the text (any case), and the state. */
export function shownRows(rows: readonly TargetRow[], filter: TargetFilter): TargetRow[] {
  const needle = filter.text.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (filter.state === 'all' || r.state === filter.state) &&
      (needle === '' ||
        r.title.toLowerCase().includes(needle) ||
        r.detail.toLowerCase().includes(needle)),
  );
}

export type Selection = ReadonlySet<string>;

export const NONE: Selection = new Set();

export function toggle(selection: Selection, key: string): Selection {
  const next = new Set(selection);
  if (!next.delete(key)) next.add(key);
  return next;
}

/** Adds every shown row; rows the filters hide are not touched. */
export function selectAllShown(selection: Selection, shown: readonly TargetRow[]): Selection {
  const next = new Set(selection);
  for (const r of shown) next.add(r.key);
  return next;
}

/** "Select all shown" on the table: the rows the filters show join the selection. */
export function selectShown(
  rows: readonly TargetRow[],
  filter: TargetFilter,
  selection: Selection,
): Selection {
  return selectAllShown(selection, shownRows(rows, filter));
}

export const clearSelection = (): Selection => NONE;

export const TOO_MANY_TEXT = `Choose at most ${BOOTSTRAP_LEDGER_MAX.toLocaleString('en-US')} at a time.`;

export const overLimit = (selection: Selection): boolean => selection.size > BOOTSTRAP_LEDGER_MAX;

/** The request's targets: the selection, each once, the table's order first. */
export function targetsOf(
  rows: readonly TargetRow[],
  selection: Selection,
): BootstrapLedgerTargetV1[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    if (selection.has(r.key) && !seen.has(r.key)) {
      seen.add(r.key);
      keys.push(r.key);
    }
  }
  for (const key of selection) if (!seen.has(key)) keys.push(key);
  return keys.map((key) => {
    const at = key.indexOf(':');
    const id = key.slice(at + 1);
    return key.startsWith('secret:') ? { secretId: id } : { documentId: id };
  });
}

export type Mark = BootstrapLedgerV1['mark'];

/** Secrets are marked "Known"; a page of a `discoverable` type "Known", of a `playable` type "Played". */
export function markChoices(
  source: 'secrets' | 'pages',
  capabilities: readonly string[],
): readonly Mark[] {
  if (source === 'secrets') return ['known'];
  return [
    ...(capabilities.includes('discoverable') ? (['known'] as const) : []),
    ...(capabilities.includes('playable') ? (['played'] as const) : []),
  ];
}

export function asOfValue(choice: string): BootstrapLedgerV1['asOf'] {
  return choice === 'before_tracking' ? 'before_tracking' : Number(choice);
}
